/**
 * localService.js — Wraps window.menuronDesktop.local.* calls.
 * Mirrors the exact same method signatures as supabase.js services
 * so components using dataService.js need zero changes.
 */

const d = window.menuronDesktop.local

export const restaurantService = {
  getMenu: (restaurantId) => d.getMenu(restaurantId),
  getTables: (restaurantId) => d.getTables(restaurantId),
  getOrders: (restaurantId) => d.getOrders(restaurantId),
  updateOrderStatus: (orderId, status) => d.updateOrderStatus(orderId, status),

  // These are only used on the web portal, not POS — fall through gracefully
  createOrder: () => ({ data: null, error: { message: 'Not supported in desktop mode' } }),
  createOrderItems: () => ({ data: null, error: { message: 'Not supported in desktop mode' } }),
  waiterConfirmOrder: () => ({ data: null, error: null }),
  getOrderById: () => ({ data: null, error: null }),
  // Menu CRUD — works offline, syncs to Supabase when back online
  createCategory: (restaurantId, data) => d.createCategory(restaurantId, data),
  updateCategory: (categoryId, updates) => d.updateCategory(categoryId, updates),
  deleteCategory: (categoryId) => d.deleteCategory(categoryId),
  createMenuItem: (restaurantId, data) => d.createMenuItem(restaurantId, data),
  updateMenuItem: (itemId, updates) => d.updateMenuItem(itemId, updates),
  deleteMenuItem: (itemId) => d.deleteMenuItem(itemId),
  getOrdersForPeriod: (restaurantId, startDate, endDate) =>
    d.getOrdersForReport(restaurantId, startDate, endDate),
}

export const billingService = {
  getSettings: (restaurantId) => d.getBillingSettings(restaurantId),
  getBillingDefaults: async (restaurantId) => {
    const res = await d.getBillingDefaults(restaurantId)
    if (res?.error || !res?.data) return res
    const row = res.data
    return {
      data: {
        gstEnabled:             !!row.gst_enabled,
        gstPercent:             parseFloat(row.gst_percent) || 0,
        gstin:                  row.gstin || null,
        serviceChargeEnabled:   !!row.service_charge_enabled,
        serviceChargePercent:   parseFloat(row.service_charge_percent) || 0,
        defaultDiscountEnabled: !!row.discount_enabled,
        defaultDiscountType:    row.discount_type || 'NONE',
        defaultDiscountValue:   parseFloat(row.discount_value) || 0,
      },
      error: null,
    }
  },
  saveSettings: (restaurantId, settings) => d.saveBillingSettings(restaurantId, settings),
  // Online: use Supabase's atomic counter directly (same as web/Android — no collision possible).
  // Offline: use local SQLite counter with OD- prefix.
  claimNextInvoiceNumber: async (restaurantId, timezone = 'Asia/Kolkata') => {
    const { isOnline } = require('./syncService')
    if (isOnline()) {
      try {
        const remote = require('./supabase')
        const result = await remote.billingService.claimNextInvoiceNumber(restaurantId, timezone)
        // Supabase returns {invoiceNumber, error} — only use if successful
        if (result?.invoiceNumber) return result
        // else fall through to offline path (network error, no invoice number returned)
      } catch {
        // fall through to offline path
      }
    }
    // Offline: always force OD- prefix via local counter
    const res = await d.claimNextInvoiceNumber(restaurantId, true)
    if (res?.data?.invoiceNumber) return { invoiceNumber: res.data.invoiceNumber, error: null }
    return { invoiceNumber: null, error: res?.error || { message: 'Failed to claim invoice number' } }
  },
}

export const posService = {
  generateOrderNumber: () => ({ data: null, error: null }),
  // When online: claim DA-prefixed number from Supabase atomically.
  // When offline: masterDb generates OD-prefixed number from local SQLite.
  createCounterOrder: async (restaurantId, opts) => {
    let orderNumber = null
    const { isOnline } = require('./syncService')
    if (isOnline()) {
      try {
        const { supabase } = require('./supabase')
        const { data, error } = await supabase.rpc('claim_next_order_number', {
          p_restaurant_id: restaurantId,
          p_prefix: 'DA',
        })
        if (!error && data) orderNumber = data
      } catch (e) {}
    }
    return d.createCounterOrder(restaurantId, { ...opts, orderNumber })
  },
  getOrderById: async (orderId) => {
    const res = await d.getOrderById(orderId)
    return { data: res?.data || null, error: res?.error || null }
  },
  getCounterOrders: (restaurantId, opts) => d.getCounterOrders(restaurantId, opts),
  getOrdersForReport: (restaurantId, startDate, endDate, managerId) =>
    d.getOrdersForReport(restaurantId, startDate, endDate, managerId),
  getManagerActivityReport: (restaurantId, startDate, endDate) =>
    d.getManagerActivityReport(restaurantId, startDate, endDate),
  getInvoices: (restaurantId) => d.getInvoices(restaurantId),
  confirmOrder: (orderId) => d.confirmOrder(orderId),
  updateStatus: (orderId, status) => d.updateStatus(orderId, status),
  billOrder: (orderId, billingData) => d.billOrder(orderId, billingData),
  updateOrderItems: (orderId, items) => d.updateOrderItems(orderId, items),
  markAsMerged: (orderId, invoiceNumber) => d.markAsMerged(orderId, invoiceNumber),
  markPaid: (orderId, paymentMethod) => d.markPaid(orderId, paymentMethod),
  deleteOrder: (orderId) => d.deleteOrder(orderId),
  markOrderInvalid: (orderId) => d.markOrderInvalid(orderId),
  updatePaymentMethod: (orderId, paymentMethod) => d.updatePaymentMethod(orderId, paymentMethod),
  claimKotNumber: (orderId) => d.claimKotNumber(orderId),
}

export const analyticsService = {
  getOrdersForPeriod: (restaurantId, startDate, endDate) =>
    d.getOrdersForReport(restaurantId, startDate, endDate),

  async getAnalyticsOverview(restaurantId, startDate, endDate, timezone = 'Asia/Kolkata') {
    const res = await d.getOrdersForReport(restaurantId, startDate.toISOString(), endDate.toISOString())
    if (res?.error) return { data: null, error: res.error }

    const orders = (res?.data || []).filter(o => o.status !== 'cancelled' && !o.is_invalid)

    const totalOrders = orders.length
    const totalRevenue = orders.reduce((s, o) => s + (parseFloat(o.total_payable ?? o.total_amount) || 0), 0)
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0

    const dailyMap = {}
    const hourlyMap = {}
    const dowMap = {}

    orders.forEach(o => {
      const d = new Date(o.created_at)
      const day = d.toLocaleDateString('en-CA', { timeZone: timezone })
      if (!dailyMap[day]) dailyMap[day] = { day, orders: 0, revenue: 0 }
      dailyMap[day].orders++
      dailyMap[day].revenue += parseFloat(o.total_payable ?? o.total_amount) || 0

      const hourStr = d.toLocaleString('en-US', { timeZone: timezone, hour: 'numeric', hour12: false })
      const hour = parseInt(hourStr) || 0
      if (!hourlyMap[hour]) hourlyMap[hour] = { hour, count: 0 }
      hourlyMap[hour].count++

      const dowStr = d.toLocaleDateString('en-US', { timeZone: timezone, weekday: 'short' })
      const dowIdx = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(dowStr)
      if (dowIdx >= 0) {
        if (!dowMap[dowIdx]) dowMap[dowIdx] = { dow: dowIdx, count: 0 }
        dowMap[dowIdx].count++
      }
    })

    return {
      data: {
        kpis: { total_orders: totalOrders, total_revenue: totalRevenue, avg_order_value: avgOrderValue },
        daily: Object.values(dailyMap).sort((a, b) => a.day.localeCompare(b.day)),
        hourly: Object.values(hourlyMap),
        dow: Object.values(dowMap),
      },
      error: null,
    }
  },

  async getAnalyticsItems(restaurantId, startDate, endDate) {
    const res = await d.getOrdersForReport(restaurantId, startDate.toISOString(), endDate.toISOString())
    if (res?.error) return { data: null, error: res.error }

    const orders = (res?.data || []).filter(o => o.status !== 'cancelled' && !o.is_invalid)

    const itemMap = {}
    const catMap = {}

    orders.forEach(o => {
      ;(o.order_items || []).forEach(oi => {
        const mi = oi.menu_items
        if (!mi) return
        const { id, name } = mi
        const qty = oi.quantity || 1
        const rev = parseFloat(oi.total_price) || (parseFloat(oi.unit_price) || 0) * qty
        if (!itemMap[id]) itemMap[id] = { id, name, quantity: 0, revenue: 0, order_count: 0 }
        itemMap[id].quantity += qty
        itemMap[id].revenue += rev
        itemMap[id].order_count++

        const catName = 'Uncategorized'
        if (!catMap[catName]) catMap[catName] = { name: catName, revenue: 0, quantity: 0 }
        catMap[catName].revenue += rev
        catMap[catName].quantity += qty
      })
    })

    const items = Object.values(itemMap).sort((a, b) => b.revenue - a.revenue)
    return {
      data: {
        best_item: items[0]?.name || null,
        items,
        categories: Object.values(catMap).sort((a, b) => b.revenue - a.revenue),
      },
      error: null,
    }
  },
}

export const crmService = {
  searchCustomers: (restaurantId, query) => d.searchCustomers(restaurantId, query),
  getCustomers: (restaurantId) => d.getCustomers(restaurantId),
  createCustomer: (restaurantId, data) => d.createCustomer(restaurantId, data),
  updateCustomer: (customerId, fields) => d.updateCustomer(customerId, fields),
  deleteCustomer: (customerId) => d.deleteCustomer(customerId),
  getCustomerOrders: (customerId) => d.getCustomerOrders(customerId),
}

export const menuService = {
  createCategory: (data) => d.createCategory(data.restaurant_id, data),
  updateCategory: (categoryId, updates) => d.updateCategory(categoryId, updates),
  deleteCategory: (categoryId) => d.deleteCategory(categoryId),
  createMenuItem: (data) => d.createMenuItem(data.restaurant_id, data),
  updateMenuItem: (itemId, updates) => d.updateMenuItem(itemId, updates),
  deleteMenuItem: (itemId) => d.deleteMenuItem(itemId),
}

const _remoteStaff = () => require('./supabase').authService

export const staffService = {
  // Reads always from local SQLite cache — fast, works offline.
  // Post-mutation UI refresh uses direct state updates (not re-fetch).
  getManagers: (restaurantId) => d.getManagers(restaurantId),
  getWaiters: (restaurantId) => d.getWaiters(restaurantId),
  getRestaurantManagers: (restaurantId) => d.getManagers(restaurantId),
  getRestaurantWaiters: (restaurantId) => d.getWaiters(restaurantId),
  // Staff writes always go to Supabase — requires internet
  addWaiter: (...args) => _remoteStaff().addWaiter(...args),
  updateWaiterStatus: (...args) => _remoteStaff().updateWaiterStatus(...args),
  deleteWaiter: (...args) => _remoteStaff().deleteWaiter(...args),
  createManager: (...args) => _remoteStaff().createManager(...args),
  updateManagerStatus: (...args) => _remoteStaff().updateManagerStatus(...args),
  deleteManager: (...args) => _remoteStaff().deleteManager(...args),
}
